/**
 * OS-file drag-and-drop helpers shared by the Documents and Upload pages.
 *
 * Dropped files are handed from the Documents page to the Upload page through
 * an in-memory stash rather than router state: File objects don't survive
 * history-state serialization reliably, and the stash degrades safely — a
 * refresh on the upload page just finds it empty.
 */

let pendingFiles: File[] | null = null;

export function stashDroppedFiles(files: File[]): void {
  pendingFiles = files.length > 0 ? files : null;
}

/** Returns the stashed files and clears the stash (idempotent on re-entry). */
export function takeDroppedFiles(): File[] {
  const files = pendingFiles ?? [];
  pendingFiles = null;
  return files;
}

/**
 * True when a drag carries OS files. Checked on dragenter/dragover, where
 * only `types`/`items.kind` are populated — `files` is only reliable at drop.
 * Safari may report "public.file-url" instead of "Files". Internal app drags
 * (document rows) set only "text/plain" and never match.
 */
export function isFileDragEvent(e: {
  dataTransfer: DataTransfer | null;
}): boolean {
  const dt = e.dataTransfer;
  if (!dt) return false;
  const types = Array.from(dt.types ?? []);
  if (types.includes("Files") || types.includes("public.file-url")) {
    return true;
  }
  const firstItem = dt.items && dt.items.length > 0 ? dt.items[0] : null;
  return firstItem?.kind === "file";
}
